const pad = (s: string): string => s.padStart(2, '0');

/** TR format: DD.MM.YYYY HH:mm:ss -> datetime-local yyyy-MM-ddTHH:mm */
const toDateTimeLocalTR = (d: string, mo: string, y: string, h: string, mi: string): string =>
  `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${mi}`;

/** US format: M/D/YYYY h:mm AM/PM -> datetime-local yyyy-MM-ddTHH:mm */
const toDateTimeLocalUS = (
  mo: string, d: string, y: string,
  h: string, mi: string, ampm: string
): string => {
  const hour = parseInt(h, 10);
  const isPm = ampm.toUpperCase() === 'PM';
  const hour24 = isPm && hour !== 12 ? hour + 12 : !isPm && hour === 12 ? 0 : hour;
  return `${y}-${pad(mo)}-${pad(d)}T${pad(String(hour24))}:${mi}`;
};

/**
 * Son İşlem Zamanı mesajından başlangıç ve bitiş tarih/saatini çıkarır.
 * TR: "Query date range: 6.03.2026 02:20:00 - 6.03.2026 02:21:23"
 * US: "Query date range: 3/7/2026 5:14:13 AM - 3/8/2026 5:21:25 AM"
 * Dönen değerler datetime-local için: yyyy-MM-ddTHH:mm
 */
export const parseDateRangeFromMessage = (mesaj: string): { beginDate: string; endDate: string } => {
  const empty = { beginDate: '', endDate: '' };
  if (!mesaj || !mesaj.trim()) return empty;

  // TR format: DD.MM.YYYY HH:mm:ss - DD.MM.YYYY HH:mm:ss
  const trRe = /(?:Query date range|Sorgu tarih aral\S*)\s*:\s*(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*-\s*(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/i;
  const trM = mesaj.match(trRe);
  if (trM) {
    return {
      beginDate: toDateTimeLocalTR(trM[1], trM[2], trM[3], trM[4], trM[5]),
      endDate: toDateTimeLocalTR(trM[7], trM[8], trM[9], trM[10], trM[11]),
    };
  }

  // US format: M/D/YYYY h:mm:ss AM/PM - M/D/YYYY h:mm:ss AM/PM
  const usRe = /(?:Query date range|Sorgu tarih aral\S*)\s*:\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s*-\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)/i;
  const usM = mesaj.match(usRe);
  if (usM) {
    return {
      beginDate: toDateTimeLocalUS(usM[1], usM[2], usM[3], usM[4], usM[5], usM[7]),
      endDate: toDateTimeLocalUS(usM[8], usM[9], usM[10], usM[11], usM[12], usM[14]),
    };
  }

  return empty;
};

export interface ParsedEStatementAlertMessage {
  vkn?: string;
  uid?: string;
  bankCode?: string;
  beginDate?: string;
  endDate?: string;
}

/**
 * Dashboard eStatement alert mesajından Vkn, Uid, BankaKodu ve tarih aralığını çıkarır.
 * Örnek: "eStatement Son İşlem Zamanı Hatası: Vkn=9990495959, Uid=zorluty, BankaKodu=46. Mesaj: Query date range: 3/7/2026 5:14:13 AM - 3/8/2026 5:21:25 AM,"
 * Tarih formatı: US M/D/YYYY h:mm:ss AM/PM
 * Dönen beginDate/endDate datetime-local için: yyyy-MM-ddTHH:mm
 */
export const parseEStatementAlertMessage = (message: string): ParsedEStatementAlertMessage => {
  const result: ParsedEStatementAlertMessage = {};
  if (!message || !message.trim()) return result;

  const vknMatch = message.match(/Vkn\s*=\s*(\d+)/i);
  if (vknMatch) result.vkn = vknMatch[1].trim();

  const uidMatch = message.match(/Uid\s*=\s*([^,\s.]+)/i);
  if (uidMatch) result.uid = uidMatch[1].trim();

  const bankMatch = message.match(/BankaKodu\s*=\s*(\d+)/i);
  if (bankMatch) result.bankCode = bankMatch[1].trim();

  // US format: 3/7/2026 5:14:13 AM - 3/8/2026 5:21:25 AM
  const dateRangeRe = /Query date range\s*:\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s*-\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)/i;
  const dateMatch = message.match(dateRangeRe);
  if (dateMatch) {
    const pad = (s: string) => s.padStart(2, '0');
    const toHour24 = (h: string, ampm: string) => {
      const hour = parseInt(h, 10);
      const isPm = ampm.toUpperCase() === 'PM';
      if (isPm && hour !== 12) return hour + 12;
      if (!isPm && hour === 12) return 0;
      return hour;
    };
    result.beginDate = `${dateMatch[3]}-${pad(dateMatch[1])}-${pad(dateMatch[2])}T${pad(String(toHour24(dateMatch[4], dateMatch[7])))}:${dateMatch[5]}`;
    result.endDate = `${dateMatch[10]}-${pad(dateMatch[8])}-${pad(dateMatch[9])}T${pad(String(toHour24(dateMatch[11], dateMatch[14])))}:${dateMatch[12]}`;
  }

  return result;
};
